import type { FilterFn } from "@tanstack/vue-table";
import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import type { Size } from "@/components/helpers/common-props";
import { debounce } from "lodash-es";
import { computed, defineComponent, ref, watch } from "vue";
import { p } from "@/utils/props-builder";
import Badge from "../../badge/Badge";
import DateTime from "../../datetime-picker/DateTime";
import FacetedFilter from "../../faceted-filter/FacetedFilter";
import { useTableEmitter } from "../composables/helpers";
import { injectTable } from "../DataTable";
import { defaultFacetedProps } from "./props";

export type DateFilter = {
  _type: "date";
  operator: "between";
  value: [Date, Date];
};

const dateProps = {
  ...defaultFacetedProps,
  size: p.string<Size | "full">("full"),
  maxValue: p.custom<number | Date | null>([Number, Date]),
  maximumDays: p.number(),
} satisfies ComponentPropsOptions;

export type DateFacetFilterProps = ExtractPublicPropTypes<typeof dateProps>;

export default defineComponent({
  name: "DateFacetFilter",
  props: dateProps,
  setup(props) {
    const key = ref(Math.random());
    const facetedFilterRef = ref<{
      close: () => void;
    } | null>(null);
    const dates = ref<[Date, Date] | null>(null);
    const table = injectTable();
    const column = table.getColumn(props.accessorKey);
    const columnFilter = computed(() => {
      const filter = column?.getFilterValue();

      if (!isDateFilter(filter)) return undefined;
      return filter;
    });

    const close = () => {
      facetedFilterRef.value?.close();
    };

    watch(
      columnFilter,
      (val) => {
        if (!val) {
          dates.value = null;
          return;
        }

        // Initialize dates from column filter if not already set
        // Column filter stores timestamps, convert them to Date objects
        if (val.value && !dates.value) {
          dates.value = val.value.map((timestamp: number | Date) =>
            timestamp instanceof Date ? timestamp : new Date(timestamp)
          ) as [Date, Date];
        }
      },
      { immediate: true }
    );

    watch(
      dates,
      debounce((val) => {
        if (!column) return;

        if (!val?.length) {
          column.setFilterValue(null);
          return;
        }

        column.setFilterValue({
          _type: "date",
          operator: "between",
          value: val,
        });
      }, props.debounce)
    );

    const Badges = computed(() => {
      key.value;
      if (!dates.value) return undefined;
      const start = new Date(dates.value[0]).toLocaleDateString();
      const end = new Date(dates.value[1]).toLocaleDateString();

      return (
        <Badge variant="muted" size="xs">
          {start} - {end}
        </Badge>
      );
    });

    useTableEmitter("set-filter", (filter) => {
      if (filter.id !== table.id || filter.column !== props.accessorKey) return;

      const newVal = filter.setter({
        value: dates.value,
      });

      if (newVal) {
        dates.value = newVal.value;
      }
    });

    return () => (
      <FacetedFilter
        label={props.label}
        size={props.size}
        ref={facetedFilterRef}
        badges={Badges.value}
        active={!!columnFilter.value}
      >
        <DateTime onConfirm={close} type="datetimerange" v-model={dates.value} onClear={close} maxValue={props.maxValue} maximumDays={props.maximumDays} />
      </FacetedFilter>
    );
  },
});

export function isDateFilter(filter: unknown): filter is DateFilter {
  if (!filter) return false;
  if (typeof filter !== "object") return false;
  return "_type" in filter && filter?._type === "date";
}

export const dateFilter: FilterFn<any> = (row, id, comparator: DateFilter) => {
  if (!comparator?.value) return true;

  const val = new Date(row.getValue(id));

  const comparatorDates = comparator.value.map((date: string | Date) => new Date(date)) as [
    Date,
    Date,
  ];

  return val >= comparatorDates[0] && val <= comparatorDates[1];
};
